import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { apiClient } from '../api/client';
import { Match } from '../types';
import { getBalanceQuality } from '../utils/calculations';

export function MatchDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scoreA, setScoreA] = useState('');
  const [scoreB, setScoreB] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadMatch(id);
    }
  }, [id]);

  const loadMatch = async (matchId: string) => {
    try {
      setError('');
      setLoading(true);
      const response = await apiClient.getMatch(matchId);
      setMatch(response.match);
      setScoreA(response.match.score_team_a?.toString() || '');
      setScoreB(response.match.score_team_b?.toString() || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveScore = async () => {
    if (!match || !id) return;

    const scoreANum = parseInt(scoreA);
    const scoreBNum = parseInt(scoreB);

    if (isNaN(scoreANum) || isNaN(scoreBNum)) {
      setError('Please enter valid scores');
      return;
    }

    if (scoreANum < 0 || scoreBNum < 0) {
      setError('Scores must be non-negative');
      return;
    }

    try {
      setError('');
      setSaving(true);
      const response = await apiClient.updateMatchScore(id, {
        score_team_a: scoreANum,
        score_team_b: scoreBNum,
      });
      setMatch(response.match);
      alert('Score updated successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update score');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Are you sure you want to delete this match?')) return;

    try {
      await apiClient.deleteMatch(id);
      navigate('/matches');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete match');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading match...</div>
      </Layout>
    );
  }

  if (!match) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400 mb-4">Match not found</div>
          <Button onClick={() => navigate('/matches')}>Back to History</Button>
        </div>
      </Layout>
    );
  }

  const balanceInfo = getBalanceQuality(match.balance.rating_difference);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">{formatDate(match.date)}</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {match.team_size}v{match.team_size} •{' '}
              {match.generation_method === 'AUTO' ? 'Auto' : 'Manual'} Generation
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={() => navigate(`/watch/${id}`)} className="flex-1 sm:flex-initial">
              ⚽ Watch Match
            </Button>
            <Button variant="secondary" onClick={() => navigate('/matches')} className="flex-1 sm:flex-initial">
              Back to History
            </Button>
            <Button variant="ghost" onClick={handleDelete} className="flex-1 sm:flex-initial">
              Delete Match
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Algorithm Used Badge */}
        {match.generation_method === 'AUTO' && match.generation_model && (
          <Card className={
            match.generation_model === 'v2' ? 'bg-purple-50 dark:bg-purple-950' :
            match.generation_model === 'v3' ? 'bg-green-50 dark:bg-green-950' :
            match.generation_model === 'super' ? 'bg-purple-50 dark:bg-purple-950' :
            match.generation_model === 'v4-beta' ? 'bg-indigo-50 dark:bg-indigo-950' :
            match.generation_model === 'super-beta' ? 'bg-purple-50 dark:bg-purple-950' :
            'bg-blue-50 dark:bg-blue-950'
          }>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Generated Using</div>
                <div className={`text-lg font-semibold ${
                  match.generation_model === 'v2' ? 'text-purple-600 dark:text-purple-400' :
                  match.generation_model === 'v3' ? 'text-green-600 dark:text-green-400' :
                  match.generation_model === 'super' ? 'text-purple-600 dark:text-purple-400' :
                  match.generation_model === 'v4-beta' ? 'text-indigo-600 dark:text-indigo-400' :
                  match.generation_model === 'super-beta' ? 'text-purple-600 dark:text-purple-400' :
                  'text-blue-600 dark:text-blue-400'
                }`}>
                  {match.generation_model === 'v1' && '📊 Algorithm V1 - Position Ratings'}
                  {match.generation_model === 'v2' && '⚡ Algorithm V2 - Raw Attributes'}
                  {match.generation_model === 'v3' && '🧬 Algorithm V3 - Chemistry & Learning'}
                  {match.generation_model === 'super' && '⚡ Super Algorithm - Multi-Dimensional'}
                  {match.generation_model === 'v4-beta' && '🎯 Model 4 BETA - Position Slider'}
                  {match.generation_model === 'super-beta' && '⚡ Super BETA - Ultimate Balance'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {match.generation_model === 'v1' && 'Balanced using FWD, MID, DEF, GK position ratings'}
                  {match.generation_model === 'v2' && 'Balanced using Technical, Physical, Mental attributes'}
                  {match.generation_model === 'v3' && 'Balanced using player chemistry and match history learning'}
                  {match.generation_model === 'super' && 'Position Balance + Attributes + Chemistry + Variety + Quality'}
                  {match.generation_model === 'v4-beta' && 'Uses position preference slider (0-100) for granular balance'}
                  {match.generation_model === 'super-beta' && 'Super Algorithm with position slider preferences (Ultimate)'}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Balance Info */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Match Balance
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* V1 Balance */}
            <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold text-blue-900 dark:text-blue-300">📊 Algorithm V1</div>
                {match.generation_model === 'v1' && (
                  <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">USED</span>
                )}
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-400 mb-2">Position-based Balance</div>
              {match.v1_balance && (() => {
                const v1BalanceInfo = getBalanceQuality(match.v1_balance.rating_difference);
                return (
                  <>
                    <div className={`text-lg font-semibold ${v1BalanceInfo.color}`}>
                      {v1BalanceInfo.emoji} {v1BalanceInfo.quality}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Diff: {match.v1_balance.rating_difference.toFixed(1)}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* V2 Balance */}
            <div className="bg-purple-50 dark:bg-purple-950 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold text-purple-900 dark:text-purple-300">🧬 Algorithm V2</div>
                {match.generation_model === 'v2' && (
                  <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">USED</span>
                )}
              </div>
              <div className="text-xs text-purple-700 dark:text-purple-400 mb-2">Attribute-based Balance</div>
              {match.v2_balance && (() => {
                const v2BalanceInfo = getBalanceQuality(match.v2_balance.rating_difference);
                return (
                  <>
                    <div className={`text-lg font-semibold ${v2BalanceInfo.color}`}>
                      {v2BalanceInfo.emoji} {v2BalanceInfo.quality}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Diff: {match.v2_balance.rating_difference.toFixed(1)}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {match.variety && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Team Variety</div>
              <div className={`text-lg font-semibold ${
                match.variety.score >= 8 ? 'text-green-600' :
                match.variety.score >= 6 ? 'text-yellow-600' :
                match.variety.score >= 4 ? 'text-orange-600' : 'text-red-600'
              }`}>
                {match.variety.score >= 8 ? '🟢' :
                 match.variety.score >= 6 ? '🟡' :
                 match.variety.score >= 4 ? '🟠' : '🔴'}
                {' '}{match.variety.label} ({match.variety.score.toFixed(1)}/10)
              </div>
            </div>
          )}
        </Card>

        {/* Algorithm Comparison */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            🔬 Algorithm Comparison
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {match.generation_method === 'AUTO'
              ? 'How each algorithm views this matchup'
              : 'How the algorithms would rate these manually-selected teams'}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Algorithm V1 - Position Ratings */}
              <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-bold text-blue-900 dark:text-blue-300">📊 Algorithm V1</div>
                  {match.generation_model === 'v1' && (
                    <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">USED</span>
                  )}
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-400 mb-3">Position Ratings Method</div>

                <div className="space-y-2">
                  <div className="bg-white dark:bg-gray-800 rounded p-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Team A</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Total:</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">{match.statistics.team_a.total_rating.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Avg:</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">{match.statistics.team_a.avg_rating.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">⚔️ FWD:</span>
                        <span className="font-medium dark:text-gray-300">{match.statistics.team_a.avg_fwd.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">⚙️ MID:</span>
                        <span className="font-medium dark:text-gray-300">{match.statistics.team_a.avg_mid.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">🛡️ DEF:</span>
                        <span className="font-medium dark:text-gray-300">{match.statistics.team_a.avg_def.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded p-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Team B</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Total:</span>
                        <span className="font-semibold text-red-600 dark:text-red-400">{match.statistics.team_b.total_rating.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Avg:</span>
                        <span className="font-semibold text-red-600 dark:text-red-400">{match.statistics.team_b.avg_rating.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">⚔️ FWD:</span>
                        <span className="font-medium dark:text-gray-300">{match.statistics.team_b.avg_fwd.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">⚙️ MID:</span>
                        <span className="font-medium dark:text-gray-300">{match.statistics.team_b.avg_mid.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">🛡️ DEF:</span>
                        <span className="font-medium dark:text-gray-300">{match.statistics.team_b.avg_def.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Algorithm V2 - Raw Attributes */}
              <div className="bg-purple-50 dark:bg-purple-950 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-bold text-purple-900 dark:text-purple-300">⚡ Algorithm V2</div>
                  {match.generation_model === 'v2' && (
                    <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">USED</span>
                  )}
                </div>
                <div className="text-xs text-purple-700 dark:text-purple-400 mb-3">Raw Attributes Method</div>

                {match.attribute_breakdown ? (
                  <div className="space-y-2">
                    <div className="bg-white dark:bg-gray-800 rounded p-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Team A</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">🎯 Technical:</span>
                          <span className="font-medium text-purple-600 dark:text-purple-400">{match.attribute_breakdown.team_a.technical.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">💪 Physical:</span>
                          <span className="font-medium text-purple-600 dark:text-purple-400">{match.attribute_breakdown.team_a.physical.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">🧠 Mental:</span>
                          <span className="font-medium text-purple-600 dark:text-purple-400">{match.attribute_breakdown.team_a.mental.toFixed(1)}</span>
                        </div>
                        {match.attribute_breakdown.team_a.goalkeeping > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">🧤 GK:</span>
                            <span className="font-medium text-purple-600 dark:text-purple-400">{match.attribute_breakdown.team_a.goalkeeping.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded p-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Team B</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">🎯 Technical:</span>
                          <span className="font-medium text-purple-600 dark:text-purple-400">{match.attribute_breakdown.team_b.technical.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">💪 Physical:</span>
                          <span className="font-medium text-purple-600 dark:text-purple-400">{match.attribute_breakdown.team_b.physical.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">🧠 Mental:</span>
                          <span className="font-medium text-purple-600 dark:text-purple-400">{match.attribute_breakdown.team_b.mental.toFixed(1)}</span>
                        </div>
                        {match.attribute_breakdown.team_b.goalkeeping > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">🧤 GK:</span>
                            <span className="font-medium text-purple-600 dark:text-purple-400">{match.attribute_breakdown.team_b.goalkeeping.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded p-3 text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Attribute breakdown not available for this match
                    </div>
                  </div>
                )}
              </div>
            </div>

          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <strong>Note:</strong> V1 uses calculated position ratings (FWD/MID/DEF/GK) while V2 analyzes raw attributes (Technical/Physical/Mental) for deeper team balancing.
              {match.generation_method === 'MANUAL' && (
                <span className="block mt-2">
                  <strong>Manual Match:</strong> These teams were hand-picked. The ratings show how the algorithms would evaluate your selections.
                </span>
              )}
            </div>
          </div>
        </Card>

        {/* Score Entry */}
        <Card>
          <h2 className="text-lg font-semibold mb-4">Match Score</h2>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Team A Score
              </label>
              <Input
                type="number"
                min="0"
                value={scoreA}
                onChange={(e) => setScoreA(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Team B Score
              </label>
              <Input
                type="number"
                min="0"
                value={scoreB}
                onChange={(e) => setScoreB(e.target.value)}
                placeholder="0"
              />
            </div>
            <Button onClick={handleSaveScore} loading={saving} className="w-full sm:w-auto">
              Save Score
            </Button>
          </div>
        </Card>

        {/* Goalkeeper Rotation Impact */}
        {match.goalkeeping && (
          <Card className="bg-blue-50 dark:bg-blue-950">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
              🧤 Goalkeeper Rotation Impact
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              {match.goalkeeping.explanation}
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Team A</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Avg GK Rating:</span>
                    <span className="font-semibold dark:text-gray-200">{match.goalkeeping.team_a.avg_gk.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Effective Rating:</span>
                    <span className="font-semibold dark:text-gray-200">{match.goalkeeping.team_a.effective_rating.toFixed(1)}</span>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-red-200 dark:border-red-800">
                <div className="text-sm font-medium text-red-900 dark:text-red-300 mb-2">Team B</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Avg GK Rating:</span>
                    <span className="font-semibold dark:text-gray-200">{match.goalkeeping.team_b.avg_gk.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Effective Rating:</span>
                    <span className="font-semibold dark:text-gray-200">{match.goalkeeping.team_b.effective_rating.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Avoided Pairings */}
        {match.variety && match.variety.avoided_pairings.length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
              🔄 Avoided Recent Pairings
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              These players often played together recently but were separated:
            </p>
            <div className="space-y-2">
              {match.variety.avoided_pairings.map((pairing, idx) => (
                <div key={idx} className="flex items-start text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
                  <div>
                    <span className="font-medium dark:text-gray-200">{pairing.player1}</span>
                    {' & '}
                    <span className="font-medium dark:text-gray-200">{pairing.player2}</span>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {pairing.percentage !== undefined
                        ? `Together ${pairing.percentage}% (${pairing.times_together}/${pairing.total_matches} matches)`
                        : `Together in ${(pairing as any).times_played_together || 0} matches (legacy data)`
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Teams Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Team A */}
          <TeamCard
            teamName="Team A"
            color="blue"
            players={match.teams.team_a}
            stats={match.statistics.team_a}
            score={match.score_team_a}
          />

          {/* Team B */}
          <TeamCard
            teamName="Team B"
            color="red"
            players={match.teams.team_b}
            stats={match.statistics.team_b}
            score={match.score_team_b}
          />
        </div>
      </div>
    </Layout>
  );
}

// Team Card Component
function TeamCard({
  teamName,
  color,
  players,
  stats,
  score,
}: {
  teamName: string;
  color: 'blue' | 'red';
  players: any[];
  stats: any;
  score: number | null;
}) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-500',
    red: 'bg-red-50 border-red-500',
  };

  const headerColors = {
    blue: 'bg-blue-600',
    red: 'bg-red-600',
  };

  return (
    <Card padding={false} className={`border-2 ${colorClasses[color]}`}>
      <div className={`${headerColors[color]} text-white px-4 py-3 flex items-center justify-between`}>
        <h3 className="text-xl font-bold">{teamName}</h3>
        {score !== null && (
          <div className="text-2xl font-bold">{score}</div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Players */}
        <div className="space-y-2">
          {players.map((player, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
            >
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">{player.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {player.overall_rating.toFixed(1)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Statistics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Total Rating:</span>
            <span className="font-semibold dark:text-gray-200">{stats.total_rating.toFixed(1)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Average Rating:</span>
            <span className="font-semibold dark:text-gray-200">{stats.avg_rating.toFixed(1)}</span>
          </div>
          <div className="border-t dark:border-gray-700 pt-2 mt-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Position Breakdown:</div>
            <div className="flex justify-between text-xs dark:text-gray-300">
              <span>⚔️ FWD:</span>
              <span>{stats.avg_fwd.toFixed(1)}</span>
            </div>
            <div className="flex justify-between text-xs dark:text-gray-300">
              <span>⚙️ MID:</span>
              <span>{stats.avg_mid.toFixed(1)}</span>
            </div>
            <div className="flex justify-between text-xs dark:text-gray-300">
              <span>🛡️ DEF:</span>
              <span>{stats.avg_def.toFixed(1)}</span>
            </div>
            {stats.avg_gk !== undefined && (
              <div className="flex justify-between text-xs dark:text-gray-300">
                <span>🧤 GK:</span>
                <span>{stats.avg_gk.toFixed(1)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
